import {
  getDocumentList,
  invalidateDocumentListData,
  searchDocumentStatus,
} from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import {
  Document,
  FilledDocumentLinkContent,
  isLinkContent,
  isRichTextContent,
  RichTextContent,
  traverseDocument,
} from "@prismicio/types-internal/lib/content"
import { useQueryClient } from "@tanstack/react-query"
import { useRef, useState } from "react"

import { env } from "../../../app/env"
import { translateDocument } from "../../../app/services/ai"
import { baseUrl, type DocumentVersionStatus } from "../../../app/services/core"
import { getReleaseId } from "../../../app/services/release"
import {
  trackDocumentCopiedToLocale,
  trackTranslateDocumentDone,
  trackTranslateDocumentStarted,
} from "../../../app/services/tracking"
import { useGoToNewDocument } from "../../../document/documentRouting"
import { useRepositoryLocalesQuery } from "../../../settings/locales/useRepositoryLocales"
import {
  createDocument,
  type DocumentData,
  type ExistingDocumentData,
  getBaseVersion,
  saveDocument,
} from "../../documentData"
import { useDocument } from "../../DocumentProvider"
import { useGoToDocument } from "../../documentRouting"
import type { DocumentDraft } from "../../useDraft"
import {
  createTranslationMap,
  findExistingLocaleVersion,
} from "./locales.utils"

export function useLocales() {
  const {
    data,
    draft,
    groupLangId,
    locale: sourceLocale,
    status,
    title,
  } = useDocument()

  const { data: langs } = useRepositoryLocalesQuery()

  const [isEnabled, setIsEnabled] = useState(true)
  const [locale, setLocale] = useState(sourceLocale)
  const [error, setError] = useState<
    { title: string; description: string } | undefined
  >()

  const translationIdRef = useRef("")

  const queryClient = useQueryClient()

  useOnChange(sourceLocale, setLocale)

  const goToDocument = useGoToDocument()
  const goToNewDocument = useGoToNewDocument()

  async function onLocaleChange(localeId: string) {
    setError(undefined)

    // We want synchronous locale switching even while all this (version loading + router navigation)
    // is happening. Otherwise it feels like the user input was not taken into account.
    setLocale(localeId)

    if (!groupLangId) {
      // Should never happen, all existing documents have a groupLangId.
      if (data.type !== "new") return

      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        locale: localeId,
      })

      return
    }

    setIsEnabled(false)

    const version = await findLocalizedVersion(groupLangId, localeId, status)

    if (version)
      goToDocument({
        id: version.doc.id,
        status: version.version.status,
        replace: false,
      })
    else {
      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        groupLangId,
        locale: localeId,
      })
    }

    setIsEnabled(true)
  }

  async function onCopyToLocale(localeId: string) {
    // We want synchronous locale switching even while all this (version loading + router navigation)
    // is happening. Otherwise it feels like the user input was not taken into account.
    setLocale(localeId)

    trackDocumentCopiedToLocale({
      customTypeId: data.customType.id,
      documentId: data.type === "existing" ? data.meta.id : undefined,
      languageFrom: sourceLocale,
      languageTo: localeId,
    })

    if (!groupLangId) {
      // Should never happen, all existing documents have a groupLangId.
      if (data.type !== "new") return

      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        locale: localeId,
      })

      return
    }

    setIsEnabled(false)

    if (data.type === "new") return

    const initialDraft = await getInitialDraft(data, localeId, status)

    const version = await findLocalizedVersion(groupLangId, localeId, status)

    if (version)
      goToDocument({
        id: version.doc.id,
        replace: false,
        status: version.version.status,
        initialDraft,
      })
    else {
      animateLocalChange(data, draft)
      goToNewDocument({
        customTypeId: data.customType.id,
        groupLangId,
        locale: localeId,
        initialDraft,
        tags: data.type === "existing" ? data.version.tags : undefined,
        title,
      })
    }

    setIsEnabled(true)
  }

  async function onTranslateToLocale(localeId: string) {
    setError(undefined)

    if (data.type === "new" || !groupLangId) {
      setError({
        title: "The translation couldn't start",
        description: "Translations are only supported for existing document.",
      })
      return
    }

    setIsEnabled(false)

    const initialDraft = await getInitialDraft(data, localeId, status)

    const translationId = crypto.randomUUID()
    translationIdRef.current = translationId

    const trackingInfo = {
      customTypeId: data.customType.id,
      documentId: data.type === "existing" ? data.meta.id : undefined,
      sourceLocale: sourceLocale,
      targetLocale: localeId,
      promptId: translationId,
    }

    trackTranslateDocumentStarted(trackingInfo)

    const started = Date.now()

    const translation = await new Promise<
      | {
          document: Document
          model: string
          cost: number
          tokensIn: number
          tokensOut: number
        }
      | undefined
    >((resolve) => {
      translateDocument({
        uuid: translationId,
        document: initialDraft,
        locale: localeId,
        onMessage: (message) => {
          switch (message.status) {
            case "translating":
              break
            case "failed":
              resolve(undefined)
              break
            case "done": {
              const { status: _, ...payload } = message
              resolve(payload)
              break
            }
          }
        },
      })
    })

    // Ignore discarded translations.
    if (translationId !== translationIdRef.current) return

    if (!translation) {
      setIsEnabled(true)
      setError({
        title: "There was an error translating the document",
        description:
          "We're still learning and improving this beta feature. Thanks for your patience!",
      })
      return
    }

    trackTranslateDocumentDone({
      ...trackingInfo,
      model: translation.model,
      sourceTokens: translation.tokensIn.toString(),
      targetTokens: translation.tokensOut.toString(),
      tokenRatio: (translation.tokensIn / translation.tokensOut).toFixed(2),
      cost: translation.cost.toFixed(2),
      time: ((Date.now() - started) / 1000).toFixed(1),
    })

    const docAndVersion = await findLocalizedVersion(
      groupLangId,
      localeId,
      status,
    )

    let newDoc:
      | {
          id: string
          status: DocumentVersionStatus
        }
      | undefined
    if (docAndVersion) {
      const { id } = docAndVersion.doc
      const baseVersion = getBaseVersion(
        docAndVersion.version,
        docAndVersion.doc.versions,
      )
      const releaseId = getReleaseId(baseVersion)
      const newVersion = await saveDocument({
        id,
        baseVersionId: baseVersion.version_id,
        releaseId,
        documentTags: new Set(docAndVersion.version.tags),
        status: baseVersion.status,
        content: translation.document,
      }).catch(() => undefined)
      newDoc = newVersion && { id, status }
    } else {
      const document = await createDocument({
        customTypeId: data.customType.id,
        locale: localeId,
        groupLangId,
        tags: data.version.tags,
        content: translation.document,
      }).catch(() => undefined)
      const version = document?.versions.find(
        ({ status }) => status === "unclassified",
      )
      newDoc = document &&
        version && { id: document.id, status: version.status }
    }

    setIsEnabled(true)

    if (!newDoc) {
      const verb = docAndVersion ? "saved" : "created"
      setError({
        title: "The translation failed",
        description: `The translated document couldn't be ${verb}.`,
      })
      return
    }

    void invalidateDocumentListData(queryClient)

    goToDocument({
      ...newDoc,
      replace: false,
    })
  }

  function cancelTranslation() {
    translationIdRef.current = ""
    setError(undefined)
    setIsEnabled(true)
    setLocale(sourceLocale)
  }

  function clearError() {
    setError(undefined)
  }

  return {
    onLocaleChange,
    isEnabled,
    locale,
    langs,
    onCopyToLocale,
    onTranslateToLocale,
    cancelTranslation,
    error,
    clearError,
  }
}

// When we are and stay in the "new doc" screen, animate the content
// to show users their locale change was taken into account.
function animateLocalChange(data: DocumentData, draft: DocumentDraft) {
  if (data.type !== "new") return

  draft.animateNextChange()
  draft.cancel()
}

function findLocalizedVersion(
  groupLangId: string,
  localeId: string,
  status: DocumentVersionStatus,
) {
  // We assume a search by group_lang_id + language is going to turn up
  // very few results so only consider the first page.
  return getDocumentList({
    baseUrl,
    authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    language: localeId,
    groupLangIds: [groupLangId],
  })
    .then((response) => findExistingLocaleVersion(response.results, status))
    .catch(() => undefined)
}

// Creates draft of doc with converted doc links
async function getInitialDraft(
  data: ExistingDocumentData,
  localeId: string,
  status: DocumentVersionStatus,
) {
  // grabs doc ids inside Link Widgets of type Document
  const docIds = getDocIds(data)

  // If there are no doc links, return original document
  if (docIds.length === 0) return Promise.resolve(data.document)

  const groupLangIds = await getGroupLangIds(docIds, status)

  const translationIds = await getTranslationIds(
    groupLangIds.map((lng) => lng.groupLangId),
    localeId,
    status,
  )

  const translationMap = createTranslationMap({
    originalDocs: docIds,
    groupLangIds,
    translationIds,
  })

  // Finds all doc links and switches to their locale version that we are translating
  // to, only if those locale versions exist
  return traverseDocument({
    document: data.document,
  })({
    transformWidget: ({ content }) => {
      if (
        isLinkContent(content) &&
        FilledDocumentLinkContent.is(content.value)
      ) {
        const newLinkId = translationMap.get(content.value.id)
        if (!newLinkId) return undefined
        return {
          ...content,
          value: {
            ...content.value,
            id: newLinkId,
          },
        }
      }

      if (isRichTextContent(content)) {
        return {
          ...content,
          value: content.value.map((node) =>
            maybeTranslateLink(translationMap, node),
          ),
        }
      }

      return content
    },
  })
}

function maybeTranslateLink(
  translationMap: Map<string, string>,
  node: RichTextContent["value"][number],
) {
  switch (node.type) {
    case "embed":
    case "image":
      return node
    default:
      if (!node.content.spans) return node
      return {
        ...node,
        content: {
          ...node.content,
          spans: node.content.spans
            .map((span) => {
              if (
                span.type !== "hyperlink" ||
                !FilledDocumentLinkContent.is(span.data)
              )
                return span

              const newLinkId = translationMap.get(span.data.id)
              if (!newLinkId) return undefined

              return {
                ...span,
                data: {
                  ...span.data,
                  id: newLinkId,
                },
              }
            })
            .filter(Boolean),
        },
      }
  }
}

function getDocIds(data: ExistingDocumentData) {
  const docIds = new Set<string>()
  traverseDocument({
    document: data.document,
  })({
    transformWidget: ({ content }) => {
      if (
        isLinkContent(content) &&
        FilledDocumentLinkContent.is(content.value)
      ) {
        docIds.add(content.value.id)
      }

      if (isRichTextContent(content)) {
        content.value.forEach((value) => {
          switch (value.type) {
            case "embed":
            case "image":
              return
            default:
              if (!value.content.spans) return
              value.content.spans.forEach((span) => {
                if (
                  span.type !== "hyperlink" ||
                  !FilledDocumentLinkContent.is(span.data)
                )
                  return
                docIds.add(span.data.id)
              })
              break
          }
        })
      }

      return content
    },
  })
  return Array.from(docIds)
}

function getGroupLangIds(documentIds: string[], status: DocumentVersionStatus) {
  return getDocumentList({
    baseUrl,
    authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    documentIds,
    statuses: searchDocumentStatus[status],
  })
    .then((response) => {
      return response.results.map((result) => ({
        id: result.id,
        groupLangId: result.group_lang_id,
      }))
    })
    .catch(() => [])
}

function getTranslationIds(
  groupLangIds: string[],
  localeId: string,
  status: DocumentVersionStatus,
) {
  return getDocumentList({
    baseUrl,
    authStrategy: env.PRISMIC_PAGE_BUILDER_AUTH_STRATEGY,
    groupLangIds,
    language: localeId,
    statuses: searchDocumentStatus[status],
  })
    .then((response) => {
      return response.results.map((result) => ({
        id: result.id,
        groupLangId: result.group_lang_id,
      }))
    })
    .catch(() => [])
}
